import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  MessageCircle,
  Search,
  Plus,
  Mic,
  Send,
  FolderOpen,
  MoreVertical,
  Trash2,
  Edit,
  MicOff,
  Check,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface Project {
  id: string;
  name: string;
  description?: string;
  created_at: string;
}

interface Conversation {
  id: string;
  title: string;
  project_id?: string;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  message: string;
  message_type: 'text' | 'audio' | 'system';
  role: 'user' | 'ai';
  created_at: string;
  audio_url?: string;
}

export default function Ariana() {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentConversation, setCurrentConversation] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputMessage, setInputMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [showAllConversations, setShowAllConversations] = useState(false);
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editingConversationTitle, setEditingConversationTitle] = useState('');
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadProjects();
      loadConversations();
    }
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('ariana_projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar projetos',
        variant: 'destructive',
      });
    }
  };

  const loadConversations = async () => {
    try {
      const { data, error } = await supabase
        .from('ariana_conversations')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar conversas',
        variant: 'destructive',
      });
    }
  };

  const loadMessages = async (conversationId: string) => {
    try {
      const { data, error } = await supabase
        .from('ariana_messages')
        .select('*')
        .eq('session_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages((data || []) as Message[]);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar mensagens',
        variant: 'destructive',
      });
    }
  };

  const createProject = async () => {
    if (!user || !newProjectName.trim()) return;

    try {
      const { data, error } = await supabase
        .from('ariana_projects')
        .insert({
          user_id: user.id,
          name: newProjectName,
          description: newProjectDescription
        })
        .select()
        .single();

      if (error) throw error;

      setProjects([data, ...projects]);
      setNewProjectName('');
      setNewProjectDescription('');
      setIsNewProjectOpen(false);
      
      toast({
        title: 'Sucesso',
        description: 'Projeto criado com sucesso',
      });
    } catch (error) {
      console.error('Erro ao criar projeto:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao criar projeto',
        variant: 'destructive',
      });
    }
  };

  const createConversation = async (projectId?: string) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('ariana_conversations')
        .insert({
          user_id: user.id,
          project_id: projectId,
          title: 'Nova Conversa'
        })
        .select()
        .single();

      if (error) throw error;

      setConversations([data, ...conversations]);
      setCurrentConversation(data.id);
      setMessages([]);
      
      toast({
        title: 'Sucesso',
        description: 'Nova conversa criada',
      });
    } catch (error) {
      console.error('Erro ao criar conversa:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao criar nova conversa',
        variant: 'destructive',
      });
    }
  };

  const sendMessage = async () => {
    if (!user || !currentConversation || (!inputMessage.trim() && !audioBlob)) return;

    try {
      let messageContent = inputMessage;
      let messageType = 'text';
      let audioUrl = null;

      if (audioBlob) {
        // Aqui você implementaria o upload do áudio para storage
        // e a transcrição via OpenAI Whisper
        messageContent = '[Mensagem de áudio]';
        messageType = 'audio';
        // audioUrl = uploaded_audio_url;
      }

      const { data, error } = await supabase
        .from('ariana_messages')
        .insert({
          session_id: currentConversation,
          user_id: user.id,
          user_email: user.email!,
          message: messageContent,
          message_type: messageType,
          audio_url: audioUrl,
          role: 'user'
        })
        .select()
        .single();

      if (error) throw error;

      setMessages([...messages, data as Message]);
      setInputMessage('');
      setAudioBlob(null);

      // Enviar para webhook via edge function e obter resposta da Ariana
      try {
        console.log('Calling ariana-webhook function with:', {
          message: messageContent,
          user_id: user.id,
          user_email: user.email,
          session_id: currentConversation
        });

        const { data: webhookResult, error: webhookError } = await supabase.functions.invoke('ariana-webhook', {
          body: {
            message: messageContent,
            user_id: user.id,
            user_email: user.email,
            session_id: currentConversation
          }
        });

        console.log('Webhook function response:', webhookResult);
        console.log('Webhook function error:', webhookError);

        if (webhookError) {
          console.error('Error calling webhook function:', webhookError);
          throw webhookError;
        }

        const arianaResponse = webhookResult?.response || 'Desculpe, não consegui processar sua mensagem no momento.';

        const { data: responseData, error: responseError } = await supabase
          .from('ariana_messages')
          .insert({
            session_id: currentConversation,
            user_id: user.id,
            user_email: user.email!,
            message: arianaResponse,
            message_type: 'text',
            role: 'ai'
          })
          .select()
          .single();

        if (!responseError) {
          setMessages(prev => [...prev, responseData as Message]);
        }
      } catch (error) {
        console.error('Erro ao enviar para webhook:', error);
        // Fallback em caso de erro
        const { data: responseData, error: responseError } = await supabase
          .from('ariana_messages')
          .insert({
            session_id: currentConversation,
            user_id: user.id,
            user_email: user.email!,
            message: 'Desculpe, estou com problemas técnicos no momento. Tente novamente.',
            message_type: 'text',
            role: 'ai'
          })
          .select()
          .single();

        if (!responseError) {
          setMessages(prev => [...prev, responseData as Message]);
        }
      }

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao enviar mensagem',
        variant: 'destructive',
      });
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao acessar microfone',
        variant: 'destructive',
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const updateConversationTitle = async (conversationId: string, newTitle: string) => {
    if (!newTitle.trim()) return;

    try {
      const { error } = await supabase
        .from('ariana_conversations')
        .update({ title: newTitle })
        .eq('id', conversationId);

      if (error) throw error;

      setConversations(conversations.map(conv => 
        conv.id === conversationId ? { ...conv, title: newTitle } : conv
      ));
      setEditingConversationId(null);
      setEditingConversationTitle('');

      toast({
        title: 'Sucesso',
        description: 'Nome da conversa atualizado',
      });
    } catch (error) {
      console.error('Erro ao atualizar conversa:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao atualizar nome da conversa',
        variant: 'destructive',
      });
    }
  };

  const startEditingConversation = (conversation: Conversation) => {
    setEditingConversationId(conversation.id);
    setEditingConversationTitle(conversation.title);
  };

  const cancelEditingConversation = () => {
    setEditingConversationId(null);
    setEditingConversationTitle('');
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedConversations = showAllConversations 
    ? filteredConversations 
    : filteredConversations.slice(0, 2);

  const hasMoreConversations = filteredConversations.length > 2;

  const getProjectName = (projectId?: string) => {
    if (!projectId) return null;
    const project = projects.find(p => p.id === projectId);
    return project?.name;
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar esquerda */}
      <div className="w-80 border-r bg-card flex flex-col">
        <div className="p-2 border-b">
          <Button 
            onClick={() => createConversation()} 
            className="w-full mb-3 bg-primary hover:bg-primary/90"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Novo chat
          </Button>
          
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar em chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Projetos */}
        <div className="p-2 border-b">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Projetos</h3>
            <Dialog open={isNewProjectOpen} onOpenChange={setIsNewProjectOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Novo Projeto</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="project-name">Nome do Projeto</Label>
                    <Input
                      id="project-name"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      placeholder="Digite o nome do projeto"
                    />
                  </div>
                  <div>
                    <Label htmlFor="project-description">Descrição</Label>
                    <Textarea
                      id="project-description"
                      value={newProjectDescription}
                      onChange={(e) => setNewProjectDescription(e.target.value)}
                      placeholder="Descrição opcional"
                    />
                  </div>
                  <Button onClick={createProject} className="w-full">
                    Criar Projeto
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="space-y-2">
            {projects.map((project) => (
              <div key={project.id} className="flex items-center space-x-2">
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm cursor-pointer hover:text-primary" 
                      onClick={() => createConversation(project.id)}>
                  {project.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Lista de conversas */}
        <ScrollArea className="flex-1">
          <div className="p-2">
            <h3 className="font-semibold text-sm mb-2">Conversas Recentes</h3>
            <div className="space-y-2">
              {displayedConversations.map((conversation) => (
              <Card 
                key={conversation.id} 
                className={`group relative transition-colors ${
                  currentConversation === conversation.id ? 'bg-primary/10' : 'hover:bg-muted'
                }`}
              >
                <CardContent className="p-3">
                  {editingConversationId === conversation.id ? (
                    <div className="flex items-center space-x-2">
                      <Input
                        value={editingConversationTitle}
                        onChange={(e) => setEditingConversationTitle(e.target.value)}
                        className="flex-1 h-8 text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            updateConversationTitle(conversation.id, editingConversationTitle);
                          }
                          if (e.key === 'Escape') {
                            cancelEditingConversation();
                          }
                        }}
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateConversationTitle(conversation.id, editingConversationTitle)}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEditingConversation}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div 
                        className="cursor-pointer"
                        onClick={() => {
                          setCurrentConversation(conversation.id);
                          loadMessages(conversation.id);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium flex-1">{conversation.title}</div>
                          {currentConversation === conversation.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingConversation(conversation);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        {conversation.project_id && (
                          <div className="text-xs text-muted-foreground mt-1">
                            📁 {getProjectName(conversation.project_id)}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(conversation.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {hasMoreConversations && !showAllConversations && (
              <Button
                variant="ghost"
                className="w-full justify-center text-sm"
                onClick={() => setShowAllConversations(true)}
              >
                <ChevronDown className="mr-2 h-4 w-4" />
                Conversas anteriores
              </Button>
            )}
            
            {showAllConversations && (
              <Button
                variant="ghost"
                className="w-full justify-center text-sm"
                onClick={() => setShowAllConversations(false)}
              >
                <ChevronUp className="mr-2 h-4 w-4" />
                Mostrar menos
              </Button>
            )}
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Área principal do chat */}
      <div className="flex-1 flex flex-col">
        {currentConversation ? (
          <>
            <div className="p-4 border-b bg-card">
              <div className="flex items-center space-x-3">
                <img 
                  src="/lovable-uploads/ecb28ef1-ce23-43fa-b89c-6e5f8a96416a.png" 
                  alt="Agente Ariana"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h1 className="text-xl font-semibold">Agente Ariana</h1>
                  <p className="text-sm text-muted-foreground">
                    Que bom te ver, {user?.email?.split('@')[0]}.
                  </p>
                </div>
              </div>
            </div>

            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4 max-w-4xl mx-auto">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <p className="text-sm">{message.message}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {new Date(message.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            <div className="p-4 border-t bg-card">
              <div className="flex items-end space-x-2 max-w-4xl mx-auto">
                {audioBlob && (
                  <div className="flex-1 p-2 bg-muted rounded-lg">
                    <p className="text-sm">Áudio gravado - pronto para enviar</p>
                  </div>
                )}
                {!audioBlob && (
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Pergunte alguma coisa..."
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  />
                )}
                
                <Button
                  variant={isRecording ? "destructive" : "outline"}
                  size="icon"
                  onClick={isRecording ? stopRecording : startRecording}
                >
                  {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
                
                <Button 
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() && !audioBlob}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <img 
                src="/lovable-uploads/ecb28ef1-ce23-43fa-b89c-6e5f8a96416a.png" 
                alt="Agente Ariana"
                className="w-24 h-24 mx-auto rounded-full object-cover mb-4"
              />
              <h2 className="text-2xl font-semibold mb-2">Agente Ariana</h2>
              <p className="text-muted-foreground mb-4">
                Selecione uma conversa ou inicie um novo chat
              </p>
              <Button onClick={() => createConversation()}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Chat
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}